/**
 * Fixed-amount coupon allocation: one order discount split equally per eligible unit.
 * Remainder cents go on the last eligible unit so line discounts sum to the order discount.
 */

export const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

export const keyForProductVariant = (p: { product_id: string; variant_id: string }) =>
  `${String(p.product_id)}:${String(p.variant_id)}`;

export type EligibleUnitLine = {
  key: string;
  quantity: number;
};

export type CouponResolveLine = {
  product_id: string;
  variant_id: string;
  eligible: boolean;
  discount_type: "PERCENTAGE" | "FIXED_AMOUNT";
  discount_value: number;
};

export type CartLineForCoupon = {
  product_id: string;
  variant_id: string;
  quantity: number;
  unitPrice: number;
};

const defaultFmtMoney = (n: number) => `$${round2(n).toFixed(2)}`;

/**
 * Split a fixed order discount equally across eligible units (cart order preserved).
 */
export function allocateFixedAmountPerUnit(
  orderDiscount: number,
  eligibleLines: EligibleUnitLine[]
): Record<string, number> {
  const result: Record<string, number> = {};
  const discount = round2(orderDiscount);
  if (discount <= 0 || !eligibleLines.length) {
    for (const line of eligibleLines) result[line.key] = 0;
    return result;
  }

  const normalized = eligibleLines.map((line) => ({
    key: line.key,
    quantity: Math.max(0, Math.floor(Number(line.quantity) || 0)),
  }));

  const totalUnits = normalized.reduce((sum, line) => sum + line.quantity, 0);
  if (totalUnits <= 0) return result;

  const totalCents = Math.round(discount * 100);
  const baseCents = Math.floor(totalCents / totalUnits);
  const remainderCents = totalCents - baseCents * totalUnits;

  let globalUnitIndex = 0;
  for (const line of normalized) {
    let lineCents = 0;
    for (let u = 0; u < line.quantity; u++) {
      globalUnitIndex += 1;
      const isLastUnit = globalUnitIndex === totalUnits;
      lineCents += baseCents + (isLastUnit ? remainderCents : 0);
    }
    result[line.key] = round2(lineCents / 100);
  }

  return result;
}

/**
 * Build per-line discount map from /discounts/resolve response.
 * FIXED_AMOUNT: one order discount, split per eligible unit.
 * PERCENTAGE: unchanged per-line percentage of line subtotal.
 */
export function computePerItemDiscountsFromResolve(
  items: CartLineForCoupon[],
  resolvedProducts: CouponResolveLine[] | undefined,
  options?: { fmtMoney?: (n: number) => string }
): { perItemMap: Record<string, number>; totalAmount: number; error: string | null } {
  const fmtMoney = options?.fmtMoney ?? defaultFmtMoney;
  const perItemMap: Record<string, number> = {};
  const lines = Array.isArray(resolvedProducts) ? resolvedProducts : [];
  const byKey = new Map(lines.map((l) => [keyForProductVariant(l), l]));

  let totalAmount = 0;
  let fixedOrderAmount: number | null = null;
  const eligibleForFixed: Array<{ key: string; quantity: number; unitPrice: number }> = [];

  for (const it of items) {
    const key = keyForProductVariant(it);
    const match = byKey.get(key);
    if (!match || !match.eligible) {
      perItemMap[key] = 0;
      continue;
    }

    const qty = Math.max(1, Number(it.quantity) || 0);
    const lineBase = round2(Number(it.unitPrice || 0) * qty);

    if (match.discount_type === "PERCENTAGE") {
      const disc = round2(lineBase * (Number(match.discount_value || 0) / 100));
      perItemMap[key] = disc;
      totalAmount += disc;
      continue;
    }

    if (fixedOrderAmount === null) {
      fixedOrderAmount = round2(Number(match.discount_value || 0));
    }
    eligibleForFixed.push({ key, quantity: qty, unitPrice: Number(it.unitPrice || 0) });
  }

  if (fixedOrderAmount !== null) {
    const eligibleSubtotal = round2(
      eligibleForFixed.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0)
    );

    if (fixedOrderAmount > eligibleSubtotal) {
      return {
        perItemMap,
        totalAmount: 0,
        error: `Coupon amount ${fmtMoney(fixedOrderAmount)} exceeds eligible product total ${fmtMoney(eligibleSubtotal)}.`,
      };
    }

    const allocated = allocateFixedAmountPerUnit(
      fixedOrderAmount,
      eligibleForFixed.map(({ key, quantity }) => ({ key, quantity }))
    );

    for (const it of items) {
      const key = keyForProductVariant(it);
      perItemMap[key] = round2(Number(allocated[key] || 0));
    }

    totalAmount = round2(
      Object.values(allocated).reduce((sum, value) => sum + Number(value || 0), 0)
    );
  }

  totalAmount = round2(totalAmount);
  return { perItemMap, totalAmount, error: null };
}

/**
 * Resolve per-line discount for tax/installation when only order total is stored.
 * Uses perItemMap when present; otherwise re-splits total per unit across lines.
 */
export function resolveLineDiscountMap(
  productLines: Array<{ key: string; quantity: number }>,
  totalDiscount: number,
  perItemMap?: Record<string, number> | null
): Record<string, number> {
  const hasMap = perItemMap && Object.values(perItemMap).some((v) => Number(v) > 0);
  if (hasMap) {
    return Object.fromEntries(
      productLines.map((line) => [line.key, round2(Number(perItemMap![line.key] || 0))])
    );
  }

  const discount = round2(totalDiscount);
  if (discount <= 0) {
    return Object.fromEntries(productLines.map((line) => [line.key, 0]));
  }

  return allocateFixedAmountPerUnit(
    discount,
    productLines.map(({ key, quantity }) => ({ key, quantity }))
  );
}

/** True when a line has a coupon / order discount amount on the row. */
export function isLineDiscountApplied(amount: number | string | null | undefined): boolean {
  return Number(amount || 0) > 0;
}

export function keyForProductVariantRow(p: { product_id?: string; variant_id?: string }) {
  return `${String(p.product_id ?? "")}:${String(p.variant_id ?? "")}`;
}

/** Per-line discount from coupon map or saved installation/sales line item. */
export function getProductLineDiscountAmount(
  product: { product_id?: string; variant_id?: string },
  perItemMap?: Record<string, number> | null,
  lineItems?: any[] | null
): number {
  const key = keyForProductVariantRow(product);
  const mapped = Number(perItemMap?.[key] ?? 0);
  if (mapped > 0) return mapped;

  const match = (lineItems || []).find(
    (li) =>
      String(li?.product_id) === String(product.product_id ?? "") &&
      String(li?.variant_id ?? "") === String(product.variant_id ?? "")
  );
  if (!match) return 0;

  return Number(
    match.discount ?? match.discount_amount ?? match.discount_on_total_quantity ?? 0
  );
}
